import { useCallback, useMemo } from 'react';
import useAnalysisSaveSummaryRichText from '../../utils/hooks/useAnalysisSaveSummaryRichText';
import useAnalysisEntityPermission from '../../utils/hooks/permissionHooks/useAnalysisEntityPermission';
import ExplainWithAiSaveButtonDisplay from './ExplainWithAiSaveButtonDisplay';
import { LlmSubjectKind } from '../../utils/apollo/graphqlSchema';
import { useAppSelector } from '../../redux/hooks';
import { analysisSelector } from '../../redux/selectors/analyses';
import { assertNotNil } from '../../utils/types';

interface IProps {
  analysisId: string;
  currentResultRichText: string;
  trackAction: string;
  trackContext: string;
}

const ExplainWithAiSaveToAnalysisButton = ({
  analysisId,
  currentResultRichText,
  trackAction,
  trackContext,
}: IProps) => {
  const analysisMemoizedSelector = useMemo(analysisSelector, []);
  const analysis = useAppSelector((state) =>
    analysisMemoizedSelector(state, analysisId),
  );
  assertNotNil(analysis);

  const { entityPermissions } = useAnalysisEntityPermission(analysis);

  const { saveSummaryRichText, saving } = useAnalysisSaveSummaryRichText({
    analysisId,
  });

  const buttonDisabled = !entityPermissions.editAnalysis || saving;

  const onSave = useCallback(async () => {
    if (buttonDisabled) {
      return;
    }
    await saveSummaryRichText(currentResultRichText);
  }, [saveSummaryRichText, currentResultRichText, buttonDisabled]);

  return (
    <ExplainWithAiSaveButtonDisplay
      subjectKind={LlmSubjectKind.Analysis}
      onClick={onSave}
      isPermitted={entityPermissions.editAnalysis}
      isDisabled={buttonDisabled}
      trackAction={trackAction}
      trackContext={trackContext}
    />
  );
};

export default ExplainWithAiSaveToAnalysisButton;
